import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { toast } from 'sonner';
import { Loader2, UserPlus, UserX, Users, Briefcase } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function SignupSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    customer_signup_enabled: true,
    provider_signup_enabled: true,
    customer_signup_message: 'Customer registration is currently disabled. Please try again later.',
    provider_signup_message: 'Provider registration is currently disabled. Please try again later.'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/admin/settings/signup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch signup settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        customer_signup_enabled: settings.customer_signup_enabled,
        provider_signup_enabled: settings.provider_signup_enabled,
        customer_signup_message: settings.customer_signup_message,
        provider_signup_message: settings.provider_signup_message
      });
      
      await axios.put(`${API}/api/admin/settings/signup?${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Signup settings saved!');
    } catch (err) {
      toast.error('Failed to save signup settings');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-amber-500" />
          Signup Settings
        </CardTitle>
        <CardDescription>
          Control who can register on the platform
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Customer Signup */}
        <div className="p-4 border rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                settings.customer_signup_enabled ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <Users className={`w-5 h-5 ${settings.customer_signup_enabled ? 'text-green-600' : 'text-red-600'}`} />
              </div>
              <div>
                <p className="font-medium">Customer Registration</p>
                <p className="text-sm text-slate-500">
                  {settings.customer_signup_enabled ? 'Customers can sign up' : 'Customer signups disabled'}
                </p>
              </div>
            </div>
            <Switch
              checked={settings.customer_signup_enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, customer_signup_enabled: checked }))}
              data-testid="customer-signup-toggle"
            />
          </div>
          
          {!settings.customer_signup_enabled && (
            <div>
              <Label htmlFor="customer-message" className="text-sm">Message to show when disabled</Label>
              <Input
                id="customer-message"
                value={settings.customer_signup_message}
                onChange={(e) => setSettings(prev => ({ ...prev, customer_signup_message: e.target.value }))}
                placeholder="Enter message for customers..."
                className="mt-1"
              />
            </div>
          )}
        </div>

        {/* Provider Signup */}
        <div className="p-4 border rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                settings.provider_signup_enabled ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <Briefcase className={`w-5 h-5 ${settings.provider_signup_enabled ? 'text-green-600' : 'text-red-600'}`} />
              </div>
              <div>
                <p className="font-medium">Provider Registration</p>
                <p className="text-sm text-slate-500">
                  {settings.provider_signup_enabled ? 'Providers can sign up' : 'Provider signups disabled'}
                </p>
              </div>
            </div>
            <Switch
              checked={settings.provider_signup_enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, provider_signup_enabled: checked }))}
              data-testid="provider-signup-toggle"
            />
          </div>
          
          {!settings.provider_signup_enabled && (
            <div>
              <Label htmlFor="provider-message" className="text-sm">Message to show when disabled</Label>
              <Input
                id="provider-message"
                value={settings.provider_signup_message}
                onChange={(e) => setSettings(prev => ({ ...prev, provider_signup_message: e.target.value }))}
                placeholder="Enter message for providers..."
                className="mt-1"
              />
            </div>
          )}
        </div>

        {/* Warning Banner */}
        {(!settings.customer_signup_enabled || !settings.provider_signup_enabled) && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <UserX className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Registration Restricted</p>
              <p>
                {!settings.customer_signup_enabled && !settings.provider_signup_enabled 
                  ? 'Both customer and provider signups are disabled.'
                  : !settings.customer_signup_enabled 
                    ? 'Customer signups are currently disabled.'
                    : 'Provider signups are currently disabled.'}
              </p>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600"
            data-testid="save-signup-settings"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : 'Save Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
